import ButtonBasic from "@rentiohq/shared-frontend/dist/components/components/ButtonBasic";
import FullScreenActivityIndicator from "@rentiohq/shared-frontend/dist/components/components/FullScreenActivityIndicator";
import { FullScreenErrorState } from "@rentiohq/shared-frontend/dist/components/components/FullScreenState";
import ListItemSeparator from "@rentiohq/shared-frontend/dist/components/components/ListItemSeparator";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import * as authSelectors from "@rentiohq/shared-frontend/dist/redux/auth/auth.selectors";
import * as paymentActions from "@rentiohq/shared-frontend/dist/redux/payment/payment.actions";
import * as paymentSelectors from "@rentiohq/shared-frontend/dist/redux/payment/payment.selectors";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import { formatDate } from "@rentiohq/shared-frontend/dist/utils/date.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import * as paymentRequestUtils from "@rentiohq/shared-frontend/dist/utils/paymentRequest.utils";
import * as paymentTypes from "@rentiohq/shared/dist/types/payment.types";
import { formatCurrency } from "@rentiohq/shared/dist/utils/number.utils";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { Navigate, useNavigate } from "react-router-dom";
import { IRootState } from "redux/reducers";
import InfoHeader from "../../../../components/InfoHeader";
import Page from "../../../../components/Page";
import withAuthenticationCheck from "../../../../hoc/withAuthenticationCheck";
import * as S from "./PaymentRequestListItem.styles";

interface IPaymentRequestListItemProps {
  paymentRequest: paymentTypes.IPaymentRequest;
}

const PaymentRequestListItem: React.FC<
  IPaymentRequestListItemProps
> = props => {
  const { paymentRequest } = props;

  const navigate = useNavigate();

  const handlePressPay = () => {
    navigate(`/payment/${paymentRequest.id}`);
  };

  // Render
  const infoItems = [];

  if (paymentRequest.status !== paymentTypes.EPaymentRequestStatus.New) {
    infoItems.push(
      getLocalizedText(
        `payment.payment_request.status.${paymentRequest.status}`.toLowerCase(),
      ),
    );
  }

  infoItems.push(
    getLocalizedText("payment_request.info.pay_before", {
      date: formatDate(paymentRequest.dueDateAt),
    }),
  );

  return (
    <S.Wrap onClick={handlePressPay}>
      <S.LeftWrap>
        <S.Title>
          {paymentRequestUtils.getTitle(paymentRequest)}
          {" - "}
          {formatCurrency(paymentRequest.amount)}
        </S.Title>

        {paymentRequest.property && (
          <S.Address>{formatAddress(paymentRequest.property)}</S.Address>
        )}

        <S.Info>{infoItems.join(" | ")}</S.Info>
      </S.LeftWrap>

      <ButtonBasic
        title={`${getLocalizedText("system.next")} ›`}
        onPress={handlePressPay}
      />
    </S.Wrap>
  );
};

const HomeBase = () => {
  const dispatch = useDispatch();

  const user = useSelector((state: IRootState) => authSelectors.getUser(state));
  const isFetchingOpenPaymentRequests = useSelector((state: IRootState) =>
    paymentSelectors.getIsFetchingPaymentRequests(
      state,
      paymentTypes.EPaymentRequestFetchType.Open,
    ),
  );
  const fetchOpenPaymentRequestError = useSelector((state: IRootState) =>
    paymentSelectors.getFetchPaymentRequestsError(
      state,
      paymentTypes.EPaymentRequestFetchType.Open,
    ),
  );
  const openPaymentRequests = useSelector((state: IRootState) =>
    paymentSelectors.getPaymentRequests(
      state,
      paymentTypes.EPaymentRequestFetchType.Open,
    ),
  );

  // Data
  const fetchData = () => {
    dispatch(
      paymentActions.getPaymentRequests.actions.start({
        fetchType: paymentTypes.EPaymentRequestFetchType.Open,
        refetch: true,
      }),
    );
  };

  // Lifecycle
  React.useEffect(() => {
    fetchData();
  }, []);

  // Event handlers
  const handlePressOpenApp = async () => {
    const url = "https://rentio.page.link/install-the-app";
    window.open(url, "_blank");
  };

  // Helpers
  const getOpenPaymentRequestsWhereIAmPayer = () => {
    if (!user) {
      return [];
    }

    return (
      openPaymentRequests?.filter(
        ({ payerAccount }) =>
          !!payerAccount &&
          user.activeAccountId === payerAccount.activeAccountId,
      ) || []
    );
  };

  // Render
  if (isFetchingOpenPaymentRequests) {
    return (
      <Page withScroll={false}>
        <FullScreenActivityIndicator />
      </Page>
    );
  }

  if (fetchOpenPaymentRequestError) {
    return (
      <Page withScroll={false}>
        <FullScreenErrorState
          showIcon={false}
          error={fetchOpenPaymentRequestError}
          title={getLocalizedText("general.error")}
          actionTitle={getLocalizedText("try.again")}
          onPressAction={fetchData}
        />
      </Page>
    );
  }

  const openPaymentRequestsWhereIAmPayer =
    getOpenPaymentRequestsWhereIAmPayer();

  if (openPaymentRequestsWhereIAmPayer.length === 0) {
    return (
      <Page>
        <InfoHeader
          title={getLocalizedText("pay.empty.title")}
          body={getLocalizedText("pay.empty.body")}
          largeButton={true}
          buttonTitle={getLocalizedText("pay.empty.cta.download_app")}
          onPressButton={handlePressOpenApp}
        />
      </Page>
    );
  }

  if (openPaymentRequestsWhereIAmPayer.length > 1) {
    return (
      <Page>
        <InfoHeader
          title={getLocalizedText("pay.multiple.title", {
            count: `${openPaymentRequestsWhereIAmPayer.length}`,
          })}
          body={getLocalizedText("pay.multiple.body", {
            count: `${openPaymentRequestsWhereIAmPayer.length}`,
          })}
        />
        <Spacer weight={ESpacerWeight.W24} />

        <S.PaymentRequestsWrap>
          {openPaymentRequestsWhereIAmPayer.map((paymentRequest, index) => (
            <>
              {index > 0 && <ListItemSeparator />}
              <PaymentRequestListItem paymentRequest={paymentRequest} />
            </>
          ))}
        </S.PaymentRequestsWrap>
      </Page>
    );
  }

  return <Navigate to={`/payment/${openPaymentRequestsWhereIAmPayer[0].id}`} />;
};

export const Home = withAuthenticationCheck(HomeBase);
